import React from 'react';
import { motion } from 'framer-motion';
import { useCreatePost } from '../hooks/useCreatePost';
import PostTypeSelector from './create-post/PostTypeSelector';
import PlatformSelector, { platforms } from './create-post/PlatformSelector';
import PointForm from './create-post/PointForm';
import PollForm from './create-post/PollForm';
import CarouselForm from './create-post/CarouselForm';
import TalkForm from './create-post/TalkForm';
import AudioForm from './create-post/AudioForm';
import LinkForm from './create-post/LinkForm';
import DraftsDropdown from './create-post/DraftsDropdown';

const CreatePostModal = ({ onClose, onPostCreated, isVideo = false }) => {
  const p = useCreatePost({ isVideo, onPostCreated, onClose });

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-50 border border-gray-300 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="create-post-modal"
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-outfit font-bold text-gray-900">Create <span className="font-arthaus">{p.postLabel}</span></h2>
            {p.draftStatus && (
              <span className="text-xs text-gray-400 flex items-center space-x-1" data-testid="draft-status">
                {p.draftStatus === 'saving' ? (
                  <><span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /><span>Saving...</span></>
                ) : (
                  <><span className="w-1.5 h-1.5 rounded-full bg-green-400" /><span>Draft saved</span></>
                )}
              </span>
            )}
          </div>
          <button data-testid="close-modal-btn" onClick={onClose} className="text-gray-600 hover:text-gray-900 text-2xl">x</button>
        </div>

        <DraftsDropdown drafts={p.drafts} expanded={p.draftsExpanded} setExpanded={p.setDraftsExpanded} onLoad={p.loadDraft} onDelete={p.deleteDraft} />

        {!isVideo && <PostTypeSelector postType={p.postType} setPostType={p.setPostType} />}

        <form onSubmit={p.handleSubmit} className="space-y-5">
          {(p.postType === 'post' || isVideo) && (
            <PointForm content={p.content} setContent={p.setContent} isVideo={isVideo} mediaFile={p.mediaFile} mediaPreview={p.mediaPreview} handleMediaChange={p.handleMediaChange} clearMedia={p.clearMedia} showMusicPicker={p.showMusicPicker} setShowMusicPicker={p.setShowMusicPicker} selectedMusic={p.selectedMusic} setSelectedMusic={p.setSelectedMusic} onFilterChange={p.handleFilterChange} />
          )}
          {p.postType === 'poll' && (
            <PollForm pollQuestion={p.pollQuestion} setPollQuestion={p.setPollQuestion} pollOptions={p.pollOptions} addPollOption={p.addPollOption} removePollOption={p.removePollOption} updatePollOption={p.updatePollOption} pollDuration={p.pollDuration} setPollDuration={p.setPollDuration} />
          )}
          {p.postType === 'carousel' && (
            <CarouselForm content={p.content} setContent={p.setContent} carouselPreviews={p.carouselPreviews} handleCarouselImageAdd={p.handleCarouselImageAdd} removeCarouselImage={p.removeCarouselImage} />
          )}
          {p.postType === 'thread' && (
            <TalkForm threadPosts={p.threadPosts} addThreadPost={p.addThreadPost} removeThreadPost={p.removeThreadPost} updateThreadPost={p.updateThreadPost} handleThreadMediaChange={p.handleThreadMediaChange} />
          )}
          {p.postType === 'audio' && (
            <AudioForm content={p.content} setContent={p.setContent} mediaFile={p.mediaFile} mediaPreview={p.mediaPreview} handleMediaChange={p.handleMediaChange} clearMedia={p.clearMedia} />
          )}
          {p.postType === 'link' && (
            <LinkForm content={p.content} setContent={p.setContent} linkPreview={p.linkPreview} setLinkPreview={p.setLinkPreview} />
          )}

          <PlatformSelector selectedPlatforms={p.selectedPlatforms} togglePlatform={p.togglePlatform} selectAllPlatforms={() => p.selectAllPlatforms(platforms)} />

          {/* Schedule Toggle */}
          <div className="border border-gray-200 rounded-xl p-3" data-testid="schedule-section">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" checked={p.isScheduled} onChange={(e) => p.setIsScheduled(e.target.checked)} className="w-4 h-4 text-[#4A90E2] rounded border-gray-300 focus:ring-[#4A90E2]" data-testid="schedule-toggle" />
              <span className="text-sm font-medium text-gray-700 flex items-center space-x-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#4A90E2]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>Schedule for later</span>
              </span>
            </label>
            {p.isScheduled && (
              <div className="flex items-center space-x-2 mt-3">
                <input type="date" value={p.scheduledDate} onChange={(e) => p.setScheduledDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="flex-1 bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] outline-none" data-testid="schedule-date" />
                <input type="time" value={p.scheduledTime} onChange={(e) => p.setScheduledTime(e.target.value)} className="flex-1 bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] outline-none" data-testid="schedule-time" />
              </div>
            )}
          </div>

          {/* Exclusive Content Toggle */}
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" checked={p.isExclusive} onChange={(e) => p.setIsExclusive(e.target.checked)} className="w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500" data-testid="exclusive-toggle" />
              <span className="text-sm font-medium text-gray-700 flex items-center space-x-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <span>Subscriber-only</span>
              </span>
            </label>
            {p.isExclusive && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-medium">Exclusive</span>}
          </div>

          <div className="flex space-x-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 rounded-full py-3 font-medium transition-colors">Cancel</button>
            <button data-testid="submit-post-btn" type="submit" disabled={p.loading} className={`flex-1 text-white hover:opacity-90 rounded-full py-3 font-bold transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 ${p.isScheduled ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7]'}`}>
              {p.loading ? (p.isScheduled ? 'Scheduling...' : 'Posting...') : p.isScheduled ? <span>Schedule <span className="font-arthaus">{p.postLabel}</span></span> : <span>Post <span className="font-arthaus">{p.postLabel}</span></span>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreatePostModal;
